import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform, useInView } from "framer-motion";
import { ArrowRight, Quote, ExternalLink, MapPin, ShieldCheck, Award, BadgeCheck, Sparkles, Phone, Star } from "lucide-react";
import { useSite } from "@/context/ThemeContext";
import { Reveal } from "@/components/Reveal";
import { getIcon } from "@/lib/icons";
import { yearsSince, learnerCount } from "@/lib/stats";
import api from "@/lib/api";
import { BrainCircuit, Bot, Code2, Box, Cpu, Binary, Rocket, Cog } from "lucide-react";

function Counter({ value, suffix = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!inView || value === 0) return;
    const dur = 1400; const t0 = performance.now();
    const step = (t) => {
      const p = Math.min((t - t0) / dur, 1);
      setN(Math.floor((1 - Math.pow(1 - p, 3)) * value));
      if (p < 1) requestAnimationFrame(step); else setN(value);
    };
    requestAnimationFrame(step);
  }, [inView, value]);
  return <span ref={ref}>{value === 0 ? "" : n.toLocaleString("en-IN")}{suffix}</span>;
}

const FLOAT_ICONS = [
  { Icon: BrainCircuit, cls: "top-[18%] left-[8%]", d: "0s", s: 46 },
  { Icon: Bot, cls: "top-[26%] right-[10%]", d: "1.2s", s: 52 },
  { Icon: Code2, cls: "bottom-[24%] left-[14%]", d: "0.6s", s: 42 },
  { Icon: Box, cls: "bottom-[30%] right-[16%]", d: "1.8s", s: 44 },
  { Icon: Cpu, cls: "top-[46%] left-[46%]", d: "0.9s", s: 40 },
  { Icon: Binary, cls: "top-[62%] right-[38%]", d: "2.2s", s: 38 },
  { Icon: Rocket, cls: "top-[12%] left-[42%]", d: "1.5s", s: 36 },
  { Icon: Cog, cls: "bottom-[16%] right-[44%]", d: "0.3s", s: 40 },
];

const RE_WORDS = ["RE·SET", "RE·BOOT", "RE·DESIGN", "RE·INVENT", "RE·NEW", "RE·DEFINE"];

function OffersSpotlight() {
  const [offers, setOffers] = useState([]);
  useEffect(() => { api.get("/offers").then(({ data }) => setOffers((data || []).filter((o) => o.active !== false))).catch(() => {}); }, []);
  const navigate = useNavigate();
  if (offers.length === 0) return null;
  return (
    <section className="relative mx-auto max-w-7xl px-5 py-16" data-testid="offers-section">
      <Reveal>
        <div className="flex items-center gap-2 mb-6">
          <Sparkles className="text-primary-c" size={20} />
          <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight text-t">Festival Offers</h2>
        </div>
      </Reveal>
      <div className="grid gap-6 md:grid-cols-2">
        {offers.map((o, i) => (
          <Reveal key={o.id} delay={i * 0.1}>
            <div className="glass rounded-3xl overflow-hidden offer-glow animate-offer-pop" data-testid={`offer-${o.id}`}>
              {o.imageUrl && <img src={o.imageUrl} alt={o.title} loading="lazy" className="w-full h-56 object-cover" />}
              <div className="p-7">
                <h3 className="font-display font-black text-2xl text-primary-c">{o.title}</h3>
                {o.subtitle && <p className="text-t mt-2">{o.subtitle}</p>}
                {(o.startDate || o.endDate) && <p className="text-xs text-muted-c mt-2">{o.startDate} {o.endDate ? `– ${o.endDate}` : ""}</p>}
                <button onClick={() => navigate("/admission")} className="btn-primary px-6 py-3 mt-5 inline-flex items-center gap-2" data-testid={`offer-cta-${o.id}`}>
                  {o.cta || "Claim Offer"} <ArrowRight size={16} />
                </button>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function Testimonials({ testimonials }) {
  return (
    <section className="relative py-16 overflow-hidden" data-testid="testimonials-section">
      <div className="mx-auto max-w-7xl px-5">
        <Reveal>
          <h2 className="font-display font-bold text-4xl sm:text-5xl tracking-tight text-t">18,000+ Learners.<br /><span className="text-primary-c">Thousands of Stories.</span></h2>
          <p className="text-muted-c mt-4 text-base max-w-2xl leading-relaxed">
            Long before Google Reviews and social media, Bits &amp; Bytes was built on something simpler — trust, results and word of mouth. Many of those stories were never written online. Today, we're bringing them into the digital world.
          </p>
        </Reveal>
      </div>
      <div className="mt-10 relative">
        <div className="flex gap-5 w-max animate-marquee">
          {[...testimonials, ...testimonials].map((t, i) => (
            <div key={i} className="glass rounded-2xl p-7 w-[340px] shrink-0" data-testid={`testimonial-${i}`}>
              <Quote className="text-primary-c mb-3" size={26} />
              <p className="text-t leading-relaxed">“{t.quote}”</p>
              <div className="mt-5 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary-c flex items-center justify-center font-display font-bold text-on-primary overflow-hidden">
                  {t.imageUrl ? <img src={t.imageUrl} alt={t.name} className="w-full h-full object-cover" /> : t.name.trim().charAt(0)}
                </div>
                <div>
                  <div className="font-semibold text-t text-sm">{t.name.trim()}</div>
                  <div className="text-xs text-muted-c">{t.course}{t.year ? ` · ${t.year}` : ""}{t.profession ? ` · ${t.profession}` : ""}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Reviews() {
  const [reviews, setReviews] = useState([]);
  useEffect(() => { api.get("/reviews?public=1").then(({ data }) => setReviews(data || [])).catch(() => {}); }, []);
  if (reviews.length === 0) return null;
  return (
    <section className="relative mx-auto max-w-7xl px-5 py-16" data-testid="reviews-section">
      <Reveal><h2 className="font-display font-bold text-4xl sm:text-5xl tracking-tight text-t">Reviews &amp; <span className="text-primary-c">Ratings.</span></h2>
        <p className="text-muted-c mt-3 text-lg">5-star reviews from our learners on Google, Just Dial &amp; more.</p></Reveal>
      <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3 mt-10">
        {reviews.map((r, i) => (
          <Reveal key={r.id} delay={(i % 3) * 0.08}>
            <div className="glass card-hover rounded-2xl p-7 h-full" data-testid={`review-${r.id}`}>
              <div className="flex items-center gap-1 text-primary-c mb-3">
                {Array.from({ length: Math.max(0, Math.min(5, parseInt(r.rating) || 5)) }).map((_, k) => <Star key={k} size={16} fill="currentColor" />)}
              </div>
              <p className="text-t leading-relaxed">"{r.text}"</p>
              <div className="mt-5 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary-c flex items-center justify-center font-display font-bold text-on-primary overflow-hidden">
                  {r.imageUrl ? <img src={r.imageUrl} alt={r.name} className="w-full h-full object-cover" /> : (r.name || "B").charAt(0)}
                </div>
                <div>
                  <div className="font-semibold text-t text-sm">{r.name}</div>
                  {r.platform && <div className="text-xs text-muted-c">via {r.platform}</div>}
                </div>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

export default function Home() {
  const { data } = useSite();
  const navigate = useNavigate();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"], layoutEffect: false });
  const yBg = useTransform(scrollYProgress, [0, 1], [0, 160]);

  if (!data) return null;
  const { content, courses, categories, testimonials } = data;
  const years = yearsSince(content.config);
  const learners = learnerCount(content.config);
  const goEnquire = (course) => navigate(`/admission?course=${encodeURIComponent(course)}`);

  const stats = [
    { value: years, suffix: "+", label: "Years of Quality Education" },
    { value: learners, suffix: "+", label: "Learners" },
    { value: courses.length, suffix: "+", label: "Courses" },
    { value: 0, suffix: "", label: "Two Centres" },
  ];

  return (
    <div className="relative z-10">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen flex items-center overflow-hidden gradient-mesh" data-testid="hero-section">
        <motion.div style={{ y: yBg }} className="absolute inset-0 pointer-events-none">
          {FLOAT_ICONS.map(({ Icon, cls, d, s }, i) => (
            <div key={i} className={`absolute ${cls} animate-float`} style={{ animationDelay: d }}>
              <Icon size={s} className="text-primary-c opacity-25" />
            </div>
          ))}
        </motion.div>

        <div className="relative mx-auto max-w-7xl px-5 pt-28 pb-16 w-full">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-3xl">
            <span className="inline-block text-xs sm:text-sm uppercase tracking-[0.25em] text-primary-c font-semibold mb-4">ISO 21001 : 2018 Certified</span>
            <h1 className="brand-script font-black text-6xl sm:text-7xl lg:text-8xl leading-[0.95] text-t">Bits <span className="text-primary-c text-glow">&amp;</span> Bytes</h1>
            <p className="font-display font-bold text-2xl sm:text-3xl text-t mt-3 tracking-tight">Training for Change<span className="text-primary-c">.</span></p>
            <p className="text-lg text-muted-c mt-5 max-w-xl">{years} Years of Quality Education · {learners.toLocaleString("en-IN")}+ Learners</p>
            <p className="font-display font-semibold text-xl text-t mt-4">Your Transformation Journey Begins Here.</p>

            <div className="flex flex-wrap gap-4 mt-8">
              <button onClick={() => navigate("/courses")} className="btn-primary px-7 py-4 inline-flex items-center gap-2" data-testid="hero-explore-courses">Explore Courses <ArrowRight size={18} /></button>
              <button onClick={() => navigate("/admission")} className="btn-ghost px-7 py-4" data-testid="hero-apply-now">Apply Now</button>
              <a href="tel:9272980098" className="btn-ghost px-6 py-4 inline-flex items-center gap-2" data-testid="hero-call"><Phone size={16} /> 9272980098</a>
            </div>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-14 max-w-4xl">
            {stats.map((s, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 + i * 0.1 }} className="glass rounded-2xl p-5" data-testid={`hero-stat-${i}`}>
                {s.value === 0 ? (
                  <div className="flex items-center gap-2 text-primary-c"><MapPin size={24} /><span className="font-display font-black text-2xl">{s.label}</span></div>
                ) : (
                  <>
                    <div className="font-display font-black text-3xl sm:text-4xl text-primary-c"><Counter value={s.value} suffix={s.suffix} /></div>
                    <div className="text-sm text-muted-c mt-1 font-medium">{s.label}</div>
                  </>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS — moved up right after hero */}
      <Testimonials testimonials={testimonials} />

      {/* REVIEWS & RATINGS */}
      <Reviews />

      {/* OFFERS */}
      <OffersSpotlight />

      {/* TRAINING FOR CHANGE */}
      <section className="relative mx-auto max-w-7xl px-5 py-24" data-testid="training-for-change">
        <Reveal>
          <span className="text-xs uppercase tracking-[0.25em] font-semibold text-primary-c">Our Philosophy</span>
          <h2 className="font-display font-black text-4xl sm:text-6xl tracking-tight text-t mt-4">Training for <span className="text-primary-c">Change.</span></h2>
        </Reveal>
        <div className="grid gap-8 lg:grid-cols-12 mt-10 items-start">
          <Reveal className="lg:col-span-7">
            <p className="text-lg text-muted-c leading-relaxed">In today's world, one thing is true: <b className="text-t">Change is constant.</b> There is rapid and constant change in the way we <b className="text-primary-c">work</b> and the <b className="text-primary-c">technology</b> we use.</p>
            <p className="text-lg text-muted-c leading-relaxed mt-4">It is our endeavour to <b className="text-t">equip</b> our students and staff to cope with the ever-changing technological environment, to become a <b className="text-primary-c">Change Agent</b> in our students' personal &amp; professional lives, and to <b className="text-t">update &amp; customize</b> our training methodology to impart <b className="text-primary-c">Quality Education</b>.</p>
          </Reveal>
          <Reveal className="lg:col-span-5" delay={0.1}>
            <div className="grid grid-cols-2 gap-3">
              {["Change", "Equip", "Change Agent", "Update & Customize", "Quality Education"].map((w, i) => (
                <div key={i} className={`glass rounded-xl p-4 text-center font-display font-bold text-t ${i === 4 ? "col-span-2 text-primary-c" : ""}`}>{w}</div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* RE MOTIF */}
      <section className="relative py-24" style={{ background: "#04060a" }} data-testid="re-motif">
        <div className="absolute inset-0 gradient-mesh opacity-70" />
        <div className="relative mx-auto max-w-7xl px-5">
          <Reveal>
            <span className="text-xs uppercase tracking-[0.25em] font-semibold text-primary-c">Reimagined for the Future</span>
            <h2 className="font-display font-black text-4xl sm:text-6xl tracking-tight text-white mt-4 max-w-3xl">The same Bits &amp; Bytes. Forever evolving.</h2>
          </Reveal>
          <div className="flex flex-wrap gap-3 mt-9">
            {RE_WORDS.map((w) => {
              const [re, rest] = w.split("·");
              return <span key={w} className="re-chip glass rounded-full px-5 py-2.5 font-display font-bold text-white"><b>{re}</b>{rest}</span>;
            })}
          </div>
          <Reveal delay={0.1}>
            <blockquote className="mt-12 max-w-3xl">
              <p className="font-display font-bold text-2xl sm:text-3xl leading-snug text-white">“Success is the time to <span className="text-primary-c">Re-Define our Goals</span>, and Failure is the time to <span className="text-primary-c">Re-Define our Methods</span>.”</p>
            </blockquote>
          </Reveal>
          <p className="text-white/70 mt-6 max-w-3xl leading-relaxed">Every 5–10 years, Bits &amp; Bytes reassesses and evolves its technology, infrastructure, courses, training methodology and learning environment — while its core commitment to <b className="text-primary-c">Quality Education</b> remains unchanged.</p>
        </div>
      </section>

      {/* WHY US */}
      <section className="relative mx-auto max-w-7xl px-5 py-24" data-testid="why-section">
        <Reveal>
          <h2 className="font-display font-bold text-4xl sm:text-5xl tracking-tight text-t max-w-2xl">Why <span className="text-primary-c">Bits &amp; Bytes</span></h2>
          <p className="text-muted-c mt-4 text-lg max-w-xl">Learning that is personal, practical and built for what comes next.</p>
        </Reveal>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4 mt-12">
          {content.whyus.map((w, i) => {
            const Icon = getIcon(w.icon);
            return (
              <Reveal key={i} delay={(i % 4) * 0.08}>
                <div className="glass card-hover rounded-2xl p-7 h-full" data-testid={`why-card-${i}`}>
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5" style={{ background: "rgba(var(--primary-rgb),0.12)" }}><Icon className="text-primary-c" size={24} /></div>
                  <h3 className="font-display font-bold text-xl text-t">{w.title}</h3>
                  <p className="text-muted-c text-sm mt-2 leading-relaxed">{w.text}</p>
                </div>
              </Reveal>
            );
          })}
        </div>

        {/* CERTIFICATIONS & AFFILIATIONS */}
        <div className="mt-8 grid gap-5 lg:grid-cols-3" data-testid="certifications-block">
          <Reveal>
            <div className="glass-strong rounded-2xl p-7 h-full text-center flex flex-col items-center justify-center offer-glow" data-testid="iso-card">
              <div className="medallion w-20 h-20 rounded-full flex items-center justify-center mb-4">
                <ShieldCheck className="text-primary-c" size={34} />
              </div>
              <div className="font-display font-black text-2xl text-primary-c leading-tight">ISO 21001:2018</div>
              <div className="text-xs uppercase tracking-[0.28em] font-bold text-t mt-1.5">Certified</div>
              <p className="text-muted-c text-sm mt-3">Quality-assured education management, independently certified.</p>
            </div>
          </Reveal>
          <Reveal delay={0.08} className="lg:col-span-2">
            <div className="glass card-hover rounded-2xl p-7 h-full">
              <div className="flex items-center gap-2 mb-4">
                <Award className="text-primary-c" size={22} />
                <h3 className="font-display font-bold text-xl text-t">Affiliations &amp; Authorisations</h3>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="rounded-xl p-5" style={{ background: "rgba(var(--primary-rgb),0.06)", border: "1px solid var(--border)" }}>
                  <div className="font-display font-bold text-t">Maharashtra Knowledge Corporation Ltd.</div>
                  <div className="text-primary-c text-sm font-semibold mt-0.5">MKCL</div>
                  <a href="https://www.mkcl.org" target="_blank" rel="noreferrer" className="text-muted-c text-xs mt-2 inline-flex items-center gap-1 hover:text-primary-c" data-testid="mkcl-link">www.mkcl.org <ExternalLink size={12} /></a>
                </div>
                <div className="rounded-xl p-5" style={{ background: "rgba(var(--primary-rgb),0.06)", border: "1px solid var(--border)" }}>
                  <div className="font-display font-bold text-t">Maharashtra State Council of Examination, Pune</div>
                  <div className="text-primary-c text-sm font-semibold mt-0.5">MSCE</div>
                  <a href="https://www.mscepune.in" target="_blank" rel="noreferrer" className="text-muted-c text-xs mt-2 inline-flex items-center gap-1 hover:text-primary-c" data-testid="msce-link">www.mscepune.in <ExternalLink size={12} /></a>
                </div>
              </div>
              <p className="text-muted-c text-sm mt-4">Authorised centre for MKCL (MS-CIT) and MSCE (GCC-TBC) certifications.</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* COURSES STRIP */}
      <section className="relative py-8" data-testid="courses-strip-section">
        <div className="mx-auto max-w-7xl px-5 flex items-end justify-between gap-4">
          <Reveal><h2 className="font-display font-bold text-4xl sm:text-5xl tracking-tight text-t">Our Courses</h2><p className="text-muted-c mt-3 text-lg">Swipe through {courses.length}+ career-focused programmes.</p></Reveal>
          <button onClick={() => navigate("/courses")} className="btn-ghost px-5 py-2.5 hidden sm:inline-flex items-center gap-2 shrink-0" data-testid="view-all-courses">View all <ArrowRight size={16} /></button>
        </div>
        <div className="mt-8 overflow-x-auto hide-scrollbar">
          <div className="flex gap-5 px-5 w-max">
            {courses.map((c) => {
              const Icon = getIcon(c.icon);
              const cat = categories.find((x) => x.id === c.category);
              return (
                <div key={c.id} className="glass card-hover rounded-2xl p-6 w-[260px] shrink-0 flex flex-col" data-testid={`course-strip-${c.id}`}>
                  {c.imageUrl ? (
                    <div className="w-full h-32 rounded-xl overflow-hidden mb-4 bg-surface"><img src={c.imageUrl} alt={c.name} loading="lazy" className="w-full h-full object-cover object-top" /></div>
                  ) : (
                    <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4" style={{ background: "rgba(var(--primary-rgb),0.12)" }}><Icon className="text-primary-c" size={22} /></div>
                  )}
                  <h3 className="font-display font-bold text-lg text-t">{c.name}</h3>
                  <span className="inline-block mt-2 text-[11px] uppercase tracking-wide font-semibold text-primary-c">{cat?.name}</span>
                  <p className="text-muted-c text-sm mt-2 flex-1">{c.description}</p>
                  <button onClick={() => goEnquire(c.name)} className="btn-primary mt-5 py-2.5 text-sm" data-testid={`enquire-${c.id}`}>Enquire</button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FACILITIES */}
      <section className="relative mx-auto max-w-7xl px-5 py-24" data-testid="facilities-section">
        <Reveal><h2 className="font-display font-bold text-4xl sm:text-5xl tracking-tight text-t">Our Facilities</h2><p className="text-muted-c mt-3 text-lg max-w-xl">Hands-on labs and future-ready technology under one roof.</p></Reveal>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 mt-12">
          {content.facilities.map((f, i) => {
            const Icon = getIcon(f.icon);
            return (
              <Reveal key={i} delay={(i % 3) * 0.08}>
                <div className="glass card-hover rounded-2xl p-7 flex items-start gap-4" data-testid={`facility-${i}`}>
                  {f.imageUrl ? (
                    <div className="w-12 h-12 rounded-xl overflow-hidden shrink-0"><img src={f.imageUrl} alt={f.title} loading="lazy" className="w-full h-full object-cover" /></div>
                  ) : (
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0" style={{ background: "rgba(var(--primary-rgb),0.12)" }}><Icon className="text-primary-c" size={24} /></div>
                  )}
                  <div><h3 className="font-display font-bold text-lg text-t">{f.title}</h3><p className="text-muted-c text-sm mt-1">{f.text}</p></div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* HERITAGE / TRUST */}
      <section className="relative mx-auto max-w-7xl px-5 py-16" data-testid="heritage-section">
        <div className="grid gap-10 lg:grid-cols-12 items-center">
          <Reveal className="lg:col-span-4 flex justify-center">
            <div className="medallion w-56 h-56 rounded-full flex flex-col items-center justify-center text-center">
              <span className="font-display font-black text-6xl text-primary-c leading-none">{years}</span>
              <span className="text-xs uppercase tracking-[0.2em] text-t mt-2 font-semibold">Years of</span>
              <span className="text-sm uppercase tracking-[0.15em] text-t font-bold">Quality Education</span>
            </div>
          </Reveal>
          <Reveal className="lg:col-span-8" delay={0.1}>
            <h2 className="font-display font-bold text-4xl sm:text-5xl tracking-tight text-t">Trusted since <span className="text-primary-c">1999.</span></h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-7">
              {[{ Icon: BadgeCheck, t: "Established 1999" }, { Icon: Award, t: "Quality Assured" }, { Icon: ShieldCheck, t: "MKCL Authorised" }, { Icon: BadgeCheck, t: "Justdial Verified" }].map((b, i) => (
                <div key={i} className="glass rounded-xl p-4 text-center">
                  <b.Icon className="text-primary-c mx-auto mb-2" size={22} />
                  <div className="text-xs font-semibold text-t">{b.t}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* PRICELESS CLOSING */}
      <section className="relative mx-auto max-w-7xl px-5 py-16" data-testid="priceless-section">
        <Reveal>
          <div className="rounded-3xl glass-strong p-10 sm:p-16 text-center gradient-mesh">
            <p className="font-display font-bold text-2xl sm:text-4xl text-t leading-snug">The Joy of Learning at the Right Institute —<br /><span className="text-primary-c text-glow font-black text-4xl sm:text-6xl">PRICELESS.</span></p>
            <div className="flex flex-wrap gap-4 justify-center mt-9">
              <button onClick={() => navigate("/admission")} className="btn-primary px-8 py-4 inline-flex items-center gap-2" data-testid="cta-apply-now">Apply Now <ArrowRight size={18} /></button>
              <a href="tel:9272980098" className="btn-ghost px-8 py-4 inline-flex items-center gap-2"><Phone size={16} /> Call / msg 9272980098</a>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
